import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock } from 'lucide-react';
import { BlogArticle, getArticleAuthor } from '@/data/blog-articles';
import { motion } from 'framer-motion';

interface BlogCardProps {
  article: BlogArticle;
  index?: number;
  animate?: boolean;
}

const BlogCard = ({ article, index = 0, animate = false }: BlogCardProps) => {
  const Wrapper = animate ? motion.div : 'div';
  const animProps = animate
    ? {
        initial: { opacity: 0, y: 20 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true },
        transition: { duration: 0.4, delay: index * 0.1 },
      }
    : {};

  return (
    <Wrapper {...(animProps as any)}>
      <Link
        to={`/blog/${article.slug}`}
        onClick={() => window.scrollTo(0, 0)}
        className="group flex flex-col h-full rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/30 transition-colors"
      >
        {/* Gradient liseret */}
        <div className="h-2 w-full bg-gradient-to-r from-primary via-primary/60 to-accent" />

        <div className="p-6 md:p-7 flex flex-col flex-1">
          {/* Badge + date + read time */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="text-xs font-heading font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary">
              {article.category}
            </span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Calendar size={12} />
              {new Date(article.date).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock size={12} /> {article.readTime}
            </span>
          </div>

          {/* Title */}
          <h3 className="font-heading text-base md:text-lg font-bold text-foreground leading-snug group-hover:text-primary transition-colors line-clamp-2">
            {article.title}
          </h3>

          {/* Excerpt */}
          <p className="mt-3 text-sm text-muted-foreground line-clamp-2 flex-1 leading-relaxed">
            {article.metaDescription}
          </p>

          {/* Footer */}
          <div className="mt-5">
            <span className="text-primary font-heading text-sm font-semibold inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
              Lire l'article <ArrowRight size={14} />
            </span>
          </div>
        </div>
      </Link>
    </Wrapper>
  );
};

export default BlogCard;
